import axios from "axios";
import { API } from "../../App";
import {
  RatingScoreCard,
  RatingDistribution,
  ReviewSubmissionForm,
  ReviewList,
} from "../../components/Reviews";

/**
 * Reviews section block for the public venue profile. Lifted out of
 * `VenueDetailPage.jsx` (was lines 807-864). Each sub-block — score card,
 * distribution, submission form, public list, owner replies — is independently
 * gated by the resolved layout via the `showSection` predicate the parent
 * passes in (so a Verified plan can show the score only, while Booking shows
 * the full UI).
 *
 * The `onChanged` callback is fired after a submit or inline edit so the
 * parent can re-fetch the list + summary and re-render with fresh state.
 *
 * @param {object}  props
 * @param {object}  props.venue
 * @param {object}  props.summary       — rating summary payload
 * @param {any[]}   props.reviews
 * @param {string}  props.lang          — i18n language code
 * @param {Function} props.t            — i18n `t()` from useTranslation
 * @param {Function} props.showSection  — (layoutKey, planKey)=>boolean
 * @param {Function} props.onChanged    — refetch list+summary
 * @param {number}  props.order         — CSS flex order for section-reorder
 */
export default function VenueReviews({
  venue,
  summary,
  reviews,
  lang,
  t,
  showSection,
  onChanged,
  order,
}) {
  const showAny =
    showSection("rating_score_card", "can_show_rating_score") ||
    showSection("rating_distribution", "can_show_public_reviews") ||
    showSection("review_submission_form", "can_receive_reviews") ||
    showSection("reviews_section", "can_show_public_reviews");

  if (!showAny) return null;

  // Leave-a-review form is COUPLED to the public review list — a venue that
  // can't show reviews publicly should not be allowed to collect them either
  // (otherwise users submit reviews nobody will see). The backend enforces
  // the same AND check on POST /api/reviews.
  const showSubmissionForm =
    showSection("review_submission_form", "can_receive_reviews") &&
    showSection("reviews_section", "can_show_public_reviews");

  const refetch = async () => {
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/venues/${venue.venue_id}/reviews?limit=20`),
        axios.get(`${API}/venues/${venue.venue_id}/rating-summary`),
      ]);
      onChanged?.({ reviews: listRes.data || [], summary: sumRes.data });
    } catch {
      /* swallow — caller can still trigger a manual refresh */
    }
  };

  return (
    <div
      data-testid="venue-reviews"
      className="space-y-5"
      style={{ order }}
    >
      <h2 className="text-xl font-medium text-foreground">{t("venue:reviews")}</h2>

      {showSection("rating_score_card", "can_show_rating_score") && (
        <RatingScoreCard summary={summary} />
      )}
      {showSection("rating_distribution", "can_show_public_reviews") && (
        <RatingDistribution summary={summary} />
      )}
      {showSubmissionForm && (
        <ReviewSubmissionForm venueId={venue.venue_id} onSubmitted={refetch} />
      )}
      {showSection("reviews_section", "can_show_public_reviews") && (
        <ReviewList
          reviews={reviews}
          showOwnerReplies={showSection("owner_replies", "can_show_owner_replies")}
          currentLang={lang}
          onReviewUpdated={refetch}
        />
      )}
    </div>
  );
}
